import { useCurrentUser } from '@/hooks/use-auth';
import { useSyncState, useUpdateSyncSchedule } from '@/hooks/use-sync';
import { get, put } from '@/lib/api';
import {
	ROLE_LABELS,
	SYNC_INTERVAL_OPTIONS,
	SYNC_STATUS_COLORS,
	SYNC_STATUS_LABELS,
	formatTimestamp,
} from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrgInfo, OrgMember } from '@uncain/shared';
import { useState } from 'react';

export function SettingsPage() {
	const { data: user } = useCurrentUser();
	const isAdmin = user?.currentOrg?.role === 'admin';

	return (
		<div className="space-y-8">
			<h1 className="text-2xl font-bold text-foreground">Settings</h1>
			<OrgInfoSection />
			<MemberSection isAdmin={isAdmin} currentUserId={user?.id} />
			<PeriodicSyncCard isAdmin={isAdmin} />
		</div>
	);
}

function OrgInfoSection() {
	const { data, isLoading } = useQuery<OrgInfo>({
		queryKey: queryKeys.org.info(),
		queryFn: () => get<OrgInfo>('/org'),
	});

	if (isLoading) return <div className="text-muted-foreground">Loading...</div>;
	if (!data) return null;

	return (
		<div className="rounded-lg border border-border bg-card p-6">
			<h2 className="mb-4 text-lg font-semibold text-foreground">Organization</h2>
			<div className="grid grid-cols-2 gap-4 text-sm">
				<div>
					<span className="text-muted-foreground">Name</span>
					<p className="font-medium text-foreground">{data.login}</p>
				</div>
				<div>
					<span className="text-muted-foreground">Installation ID</span>
					<p className="font-medium text-foreground">{data.installationId}</p>
				</div>
				<div>
					<span className="text-muted-foreground">Installed At</span>
					<p className="font-medium text-foreground">{formatTimestamp(data.installedAt)}</p>
				</div>
				<div>
					<span className="text-muted-foreground">Last Updated</span>
					<p className="font-medium text-foreground">{formatTimestamp(data.updatedAt)}</p>
				</div>
			</div>
		</div>
	);
}

function MemberSection({
	isAdmin,
	currentUserId,
}: {
	isAdmin: boolean;
	currentUserId?: string;
}) {
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery<{ members: OrgMember[] }>({
		queryKey: queryKeys.org.members(),
		queryFn: () => get<{ members: OrgMember[] }>('/org/members'),
	});

	const updateRole = useMutation({
		mutationFn: ({ userId, role }: { userId: string; role: string }) =>
			put(`/org/members/${userId}/role`, { role }),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.org.all }),
	});

	if (isLoading) return <div className="text-muted-foreground">Loading members...</div>;

	const members = data?.members ?? [];

	return (
		<div className="rounded-lg border border-border bg-card p-6">
			<h2 className="mb-4 text-lg font-semibold text-foreground">Members</h2>
			{members.length === 0 ? (
				<p className="text-sm text-muted-foreground">No members found.</p>
			) : (
				<div className="space-y-3">
					{members.map((member) => (
						<MemberRow
							key={member.userId}
							member={member}
							isAdmin={isAdmin}
							isCurrentUser={member.userId === currentUserId}
							onRoleChange={(role) => updateRole.mutate({ userId: member.userId, role })}
							isPending={updateRole.isPending}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function MemberRow({
	member,
	isAdmin,
	isCurrentUser,
	onRoleChange,
	isPending,
}: {
	member: OrgMember;
	isAdmin: boolean;
	isCurrentUser: boolean;
	onRoleChange: (role: string) => void;
	isPending: boolean;
}) {
	const [editing, setEditing] = useState(false);
	const [selectedRole, setSelectedRole] = useState(member.role);

	return (
		<div className="flex items-center justify-between rounded border border-border p-3">
			<div>
				<span className="text-sm font-medium text-foreground">
					{member.login}
					{isCurrentUser && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
				</span>
				{member.email && <p className="text-xs text-muted-foreground">{member.email}</p>}
			</div>
			<div className="flex items-center gap-3">
				{editing ? (
					<>
						<select
							value={selectedRole}
							onChange={(e) => setSelectedRole(e.target.value)}
							className="rounded border border-border bg-background px-2 py-1 text-sm"
						>
							<option value="viewer">Viewer</option>
							<option value="operator">Operator</option>
							<option value="admin">Admin</option>
						</select>
						<button
							type="button"
							onClick={() => {
								onRoleChange(selectedRole);
								setEditing(false);
							}}
							disabled={isPending}
							className="text-xs text-primary hover:underline"
						>
							Save
						</button>
						<button
							type="button"
							onClick={() => setEditing(false)}
							className="text-xs text-muted-foreground hover:underline"
						>
							Cancel
						</button>
					</>
				) : (
					<>
						<span className="text-sm text-muted-foreground">
							{ROLE_LABELS[member.role] ?? member.role}
						</span>
						{isAdmin && !isCurrentUser && (
							<button
								type="button"
								onClick={() => setEditing(true)}
								className="text-xs text-primary hover:underline"
							>
								Change
							</button>
						)}
					</>
				)}
			</div>
		</div>
	);
}

// ── PeriodicSyncCard ────────────────────────────────────────────────────

function PeriodicSyncCard({ isAdmin }: { isAdmin: boolean }) {
	const { data: syncState } = useSyncState();
	const updateSchedule = useUpdateSyncSchedule();

	const schedule = syncState?.schedule;
	const isEnabled = schedule?.enabled ?? false;
	const intervalSeconds = schedule?.intervalSeconds ?? 86400;

	const handleToggle = () => {
		if (!isAdmin) return;
		updateSchedule.mutate({ enabled: !isEnabled, intervalSeconds });
	};

	const handleIntervalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!isAdmin) return;
		updateSchedule.mutate({ enabled: true, intervalSeconds: Number(e.target.value) });
	};

	return (
		<div className="rounded-lg border border-border bg-card p-6">
			<h2 className="mb-4 text-lg font-semibold text-foreground">Periodic Sync</h2>
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<div>
						<p className="text-sm font-medium text-foreground">Automatic resync</p>
						<p className="text-xs text-muted-foreground">
							Periodically rebuild the trust graph to detect missed changes
						</p>
					</div>
					<button
						type="button"
						onClick={handleToggle}
						disabled={!isAdmin || updateSchedule.isPending}
						className={cn(
							'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
							isEnabled ? 'bg-primary' : 'bg-muted',
							!isAdmin && 'cursor-not-allowed opacity-50',
						)}
					>
						<span
							className={cn(
								'inline-block h-4 w-4 rounded-full bg-white transition-transform',
								isEnabled ? 'translate-x-6' : 'translate-x-1',
							)}
						/>
					</button>
				</div>

				{isEnabled && (
					<div className="flex items-center gap-4">
						<label htmlFor="sync-interval" className="text-sm text-muted-foreground">
							Interval:
						</label>
						<select
							id="sync-interval"
							value={intervalSeconds}
							onChange={handleIntervalChange}
							disabled={!isAdmin || updateSchedule.isPending}
							className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-50"
						>
							{SYNC_INTERVAL_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>
				)}

				<div className="flex items-center gap-4 text-xs text-muted-foreground">
					{syncState && (
						<span className={cn(SYNC_STATUS_COLORS[syncState.status])}>
							{SYNC_STATUS_LABELS[syncState.status] ?? syncState.status}
						</span>
					)}
					{schedule?.nextSyncAt && <span>Next sync: {formatTimestamp(schedule.nextSyncAt)}</span>}
				</div>
			</div>
		</div>
	);
}
